import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { rowKey, fmtMXN, fmtDate, todayLocal, EXPENSE_CATEGORIES, EXPENSE_STATUS, EXPENSE_FUNDED_BY, EXPENSE_PAYMENT_METHODS } from "@/lib/api";
import PageHeader, { StatusBadge } from "@/components/PageHeader";
import ReceiptViewer from "@/components/ReceiptViewer";
import EvidenceGallery from "@/components/EvidenceGallery";
import { useAuth } from "@/context/AuthContext";
import { Plus, Upload, Check, XCircle, Trash2, Paperclip, CloudOff, Sparkles, Loader2, Camera, FolderOpen, HardHat, Building2, Pencil, History, Download, MoreVertical, ChevronDown, Search } from "lucide-react";
import { Modal, In, Sel } from "./Projects";
import { enqueue } from "@/lib/offlineQueue";
import { flushQueue } from "@/lib/syncManager";
import { toast } from "sonner";

const PAGE_SIZE = 50;
import AdminExpensesTab from "./AdminExpensesTab";
import ChangeHistory from "@/components/ChangeHistory";

const empty = {
  project_id: "", category: "materiales", description: "", amount: 0,
  vendor_id: "", vendor: "", invoice_number: "", expense_date: todayLocal(), receipt_path: "",
  evidence_photos: [],
  funded_by: "empresa", funded_by_name: "", funded_by_note: "",
  payment_method: "efectivo",
  cash_settlement_user_id: "",
  budget_item_id: "",
  is_recurring: false,
  requires_invoice: false,
  invoice_not_required: false,
  items: [],
  invoice_details: { rfc_emisor: "", emisor_name: "", folio_fiscal: "", forma_pago: "", uso_cfdi: "", subtotal: "", iva: "", total: "" },
};

const emptyFiscal = { rfc_emisor: "", emisor_name: "", folio_fiscal: "", forma_pago: "", uso_cfdi: "", subtotal: "", iva: "", total: "" };

export default function Expenses({ lockedProjectId = null } = {}) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (lockedProjectId || searchParams.get("tab") !== "admin") ? "obra" : "admin";
  const [tab, setTab] = useState(initialTab);
  const setTabAndUrl = (t) => {
    setTab(t);
    if (t === "admin") setSearchParams({ tab: "admin" }, { replace: true });
    else setSearchParams({}, { replace: true });
  };
  // Only roles allowed to view admin tab
  const canSeeAdminTab = !lockedProjectId && ["admin", "contador", "auxiliar"].includes(user?.role);
  // If user is auxiliar, default them to admin tab as it is their main job
  useEffect(() => {
    if (lockedProjectId) return;
    if (user?.role === "auxiliar" && !searchParams.get("tab")) {
      setTab("admin");
      setSearchParams({ tab: "admin" }, { replace: true });
    }
    // eslint-disable-next-line
  }, [user?.role, lockedProjectId]);

  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);

  const canAssignCash = ["admin", "contador", "auxiliar"].includes(user?.role);
  useEffect(() => {
    if (!canAssignCash) return;
    api.get("/cash/holders").then(({ data }) => setCashHolders(data || [])).catch(() => {});
    // eslint-disable-next-line
  }, [canAssignCash]);
  const [vendors, setVendors] = useState([]);
  const [cashHolders, setCashHolders] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...empty, project_id: lockedProjectId || "" });
  const [filter, setFilter] = useState(lockedProjectId || "");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterPayment, setFilterPayment] = useState("");
  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");
  const fileRef = useRef();
  const evidenceRef = useRef();
  const topScrollRef = useRef(null);
  const bodyScrollRef = useRef(null);
  const [tableScrollW, setTableScrollW] = useState(0);
  const scanRef = useRef();
  const cameraRef = useRef();
  const fabCameraRef = useRef();
  const [receiptFile, setReceiptFile] = useState(null);
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanInfo, setScanInfo] = useState(null);
  const [budgetItems, setBudgetItems] = useState([]);
  const [historyExpense, setHistoryExpense] = useState(null);
  const [investors, setInvestors] = useState([]);

  // Cargar inversionistas (catálogo de personas que aportan recursos al proyecto)
  useEffect(() => {
    api.get("/investors/names").then(({ data }) => setInvestors(data || [])).catch(() => setInvestors([]));
  }, []);

  // Barra de desplazamiento horizontal superior sincronizada con la tabla
  useEffect(() => {
    const el = bodyScrollRef.current;
    if (!el) return;
    const update = () => setTableScrollW(el.scrollWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  });
  const syncScroll = (from, to) => () => {
    if (from.current && to.current && to.current.scrollLeft !== from.current.scrollLeft) {
      to.current.scrollLeft = from.current.scrollLeft;
    }
  };

  // Cargar partidas del presupuesto cuando se selecciona un proyecto
  useEffect(() => {
    if (!form.project_id || !open) {
      setBudgetItems([]);
      return;
    }
    let active = true;
    api.get(`/projects/${form.project_id}/budget`).then(({ data }) => {
      if (active) setBudgetItems((data.items || []).filter((it) => it.level === "item"));
    }).catch(() => active && setBudgetItems([]));
    return () => { active = false; };
  }, [form.project_id, open]);

  const canApprove = ["admin", "supervisor", "contador", "auxiliar"].includes(user?.role);

  const [search, setSearch] = useState("");
  const [savingExpense, setSavingExpense] = useState(false);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [sums, setSums] = useState(null);
  const [sort, setSort] = useState(null); // {key, dir: 1|-1}
  const toggleSort = (key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 1 };
      if (prev.dir === 1) return { key, dir: -1 };
      return null;
    });
  };

  const load = useCallback(async () => {
    const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
    if (filter) params.project_id = filter;
    if (filterStatus) params.status = filterStatus;
    if (filterCategory) params.category = filterCategory;
    if (filterPayment) params.payment_method = filterPayment;
    if (filterStart) params.start_date = filterStart;
    if (filterEnd) params.end_date = filterEnd;
    if (search.trim()) params.q = search.trim();
    if (sort) { params.sort_key = sort.key; params.sort_dir = sort.dir; }
    const [e, p, v] = await Promise.all([api.get("/expenses", { params }), api.get("/projects"), api.get("/vendors")]);
    setExpenses(e.data.items || []);
    setTotalCount(e.data.total || 0);
    setSums(e.data.sums || null);
    setProjects(p.data);
    setVendors(v.data);
  }, [page, filter, filterStatus, filterCategory, filterPayment, filterStart, filterEnd, search, sort]);
  useEffect(() => { setPage(0); /* eslint-disable-next-line */ }, [filter, filterStatus, filterCategory, filterPayment, filterStart, filterEnd, search, sort]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  // Auto-open "Nuevo gasto" modal cuando viene con ?new=1 (desde Quick Actions)
  useEffect(() => {
    if (searchParams.get("new") === "1" && projects.length > 0) {
      setEditingId(null);
      setForm({ ...empty, project_id: lockedProjectId || projects[0]?.id || "" });
      setReceiptFile(null);
      setEvidenceFiles([]);
      setScanInfo(null);
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [projects.length, searchParams, lockedProjectId]);

  const clearFilters = () => {
    setFilter(""); setFilterStatus(""); setFilterCategory(""); setFilterPayment(""); setFilterStart(""); setFilterEnd(""); setSearch("");
  };

  const handleScan = async (file) => {
    if (!file) return;
    if (!navigator.onLine) {
      toast.error("Necesitas internet para usar el escáner con IA.");
      return;
    }
    setScanning(true);
    setScanInfo(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/expenses/scan-receipt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      // Matching contra el directorio: RFC exacto + similitud de nombre (propone, nunca vincula solo)
      let matchedVendorId = "";
      let proposal = null;
      try {
        const { data: match } = await api.get("/vendors/match", { params: { rfc: data.rfc || undefined, name: data.vendor || undefined } });
        if (match.rfc_match) proposal = { ...match.rfc_match, via: "RFC" };
        else if (match.name_matches?.length) proposal = { ...match.name_matches[0], via: "nombre" };
      } catch { /* matching no disponible */ }
      if (!proposal && data.vendor) {
        const v = vendors.find((vv) => vv.name && vv.name.toLowerCase().trim() === data.vendor.toLowerCase().trim());
        if (v) proposal = { ...v, via: "nombre" };
      }
      setForm((f) => ({
        ...f,
        project_id: lockedProjectId || f.project_id,
        amount: data.amount ?? f.amount,
        vendor: matchedVendorId ? "" : (data.vendor || f.vendor),
        vendor_id: matchedVendorId || f.vendor_id,
        invoice_number: data.invoice_number || f.invoice_number,
        expense_date: data.expense_date || f.expense_date,
        category: data.category || f.category,
        description: data.description || f.description,
        items: (data.items || []).length > 0 ? data.items.map((it) => ({ _k: rowKey(), qty: it.qty, product: it.product, unit: it.unit || "", unit_price: it.unit_price, amount: it.amount })) : f.items,
        invoice_details: {
          ...emptyFiscal,
          ...f.invoice_details,
          rfc_emisor: data.rfc || f.invoice_details?.rfc_emisor || "",
          emisor_name: data.vendor || f.invoice_details?.emisor_name || "",
          folio_fiscal: data.folio_fiscal || f.invoice_details?.folio_fiscal || "",
          forma_pago: data.forma_pago || f.invoice_details?.forma_pago || "",
          uso_cfdi: data.uso_cfdi || f.invoice_details?.uso_cfdi || "",
          subtotal: data.subtotal ?? f.invoice_details?.subtotal ?? "",
          iva: data.iva ?? f.invoice_details?.iva ?? "",
          total: data.amount ?? f.invoice_details?.total ?? "",
        },
      }));
      // Auto-attach the scanned image as the receipt
      setReceiptFile(file);
      setScanInfo({
        vendor: data.vendor,
        rfc: data.rfc,
        amount: data.amount,
        matchedVendor: false,
        proposal,
      });
      // Si el modal estaba cerrado (vienes del FAB del proyecto), ábrelo con los datos
      if (!open) {
        setEditingId(null);
        setOpen(true);
      }
      toast.success("Ticket escaneado. Revisa y ajusta los datos si es necesario.");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Error al escanear";
      toast.error(typeof detail === "string" ? detail : "Error al escanear el ticket");
    } finally {
      setScanning(false);
    }
  };

  const save = async (ev) => {
    ev.preventDefault();
    if (savingExpense) return;
    setSavingExpense(true);
    try {
      await doSave();
    } finally {
      setSavingExpense(false);
    }
  };

  const doSave = async () => {
    // Generate ONE idempotency id for this submit attempt. It survives offline-queue
    // retries and online double-clicks so the backend can dedupe.
    const clientOpId = (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
      `op-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const cleanItems = (form.items || [])
      .filter((it) => (it.product || "").trim())
      .map((it) => {
        const qty = Number(it.qty) || 1;
        const up = Number(it.unit_price) || 0;
        return { qty, product: it.product.trim(), unit: (it.unit || "").trim().toUpperCase() || null, unit_price: up, amount: Number(it.amount) || Math.round(qty * up * 100) / 100 };
      });
    const fd = form.invoice_details || {};
    const hasFiscal = Object.values(fd).some((v) => v !== "" && v != null);
    const basePayload = {
      ...form,
      items: cleanItems,
      invoice_details: hasFiscal ? {
        rfc_emisor: fd.rfc_emisor?.trim() || null,
        emisor_name: fd.emisor_name?.trim() || null,
        folio_fiscal: fd.folio_fiscal?.trim() || null,
        forma_pago: fd.forma_pago?.trim() || null,
        uso_cfdi: fd.uso_cfdi?.trim() || null,
        subtotal: fd.subtotal === "" || fd.subtotal == null ? null : Number(fd.subtotal),
        iva: fd.iva === "" || fd.iva == null ? null : Number(fd.iva),
        total: fd.total === "" || fd.total == null ? null : Number(fd.total),
      } : null,
      amount: Math.round(Number(form.amount) * 100) / 100,
      client_op_id: clientOpId,
    };
    Object.keys(basePayload).forEach((k) => basePayload[k] === "" && delete basePayload[k]);
    if (canAssignCash) {
      basePayload.cash_settlement_user_id = (form.payment_method === "efectivo" && form.funded_by === "empresa") ? (form.cash_settlement_user_id || "") : "";
    } else {
      delete basePayload.cash_settlement_user_id;
    }

    if (!navigator.onLine) {
      // Offline edit not supported (PATCH on edit not queued)
      if (editingId) {
        toast.error("Sin red: no se puede editar sin conexión. Intenta cuando vuelva la red.");
        return;
      }
      // Offline: enqueue without uploading the receipt; the queue will upload it later
      await enqueue("expense", {
        ...basePayload,
        receipt_path: null,
        _receipt_blob: receiptFile || null,
        _receipt_name: receiptFile?.name || null,
      });
      toast.success("Sin conexión: gasto guardado en cola.");
      setOpen(false);
      setEditingId(null);
      setForm(empty);
      setReceiptFile(null);
      setEvidenceFiles([]);
      return;
    }
    try {
      let receiptId = form.receipt_path;
      if (receiptFile) {
        const fd = new FormData();
        fd.append("file", receiptFile);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        receiptId = data.id;
      }
      let evidenceIds = form.evidence_photos || [];
      for (const f of evidenceFiles) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        evidenceIds = [...evidenceIds, data.id];
      }
      const payload = { ...basePayload, receipt_path: receiptId || null, evidence_photos: evidenceIds };
      if (editingId) {
        // PATCH: solo enviar campos editables (excluir project_id/client_op_id/id)
        const { client_op_id: _ignore1, project_id: _ignore2, ...editable } = payload;
        await api.patch(`/expenses/${editingId}`, editable);
        toast.success("Gasto actualizado");
      } else {
        await api.post("/expenses", payload);
      }
      flushQueue();
      setOpen(false);
      setEditingId(null);
      setForm(empty);
      setReceiptFile(null);
      setEvidenceFiles([]);
      setScanInfo(null);
      load();
    } catch (err) {
      if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
        if (editingId) {
          toast.error("Sin red: no se puede editar sin conexión. Intenta cuando vuelva la red.");
          return;
        }
        await enqueue("expense", {
          ...basePayload,
          receipt_path: null,
          _receipt_blob: receiptFile || null,
          _receipt_name: receiptFile?.name || null,
        });
        toast.warning("Sin red: gasto puesto en cola.");
        setOpen(false);
        setEditingId(null);
        setForm(empty);
        setReceiptFile(null);
      setEvidenceFiles([]);
      } else {
        toast.error(err.response?.data?.detail || "Error al guardar");
      }
    }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/expenses/${id}`, { status });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar gasto?")) return;
    await api.delete(`/expenses/${id}`);
    load();
  };

  const startEdit = (expense) => {
    setEditingId(expense.id);
    setForm({
      project_id: expense.project_id || "",
      category: expense.category || "materiales",
      description: expense.description || "",
      amount: expense.amount ?? 0,
      vendor_id: expense.vendor_id || "",
      vendor: expense.vendor || "",
      invoice_number: expense.invoice_number || "",
      expense_date: expense.expense_date || todayLocal(),
      receipt_path: expense.receipt_path || "",
      evidence_photos: expense.evidence_photos || [],
      funded_by: expense.funded_by || "empresa",
      funded_by_name: expense.funded_by_name || "",
      funded_by_note: expense.funded_by_note || "",
      payment_method: expense.payment_method || "efectivo",
      cash_settlement_user_id: expense.cash_settlement_user_id || "",
      budget_item_id: expense.budget_item_id || "",
      is_recurring: !!expense.is_recurring,
      requires_invoice: !!expense.requires_invoice,
      invoice_not_required: !!expense.invoice_not_required,
      items: (expense.items || []).map((it) => ({ _k: rowKey(), qty: it.qty, product: it.product, unit: it.unit || "", unit_price: it.unit_price, amount: it.amount })),
      invoice_details: { ...emptyFiscal, ...(expense.invoice_details || {}) },
    });
    setReceiptFile(null);
    setEvidenceFiles([]);
    setScanInfo(null);
    setOpen(true);
  };

  const canEditExpense = (expense) => {
    const role = user?.role;
    if (!role) return false;
    if (role === "admin") return true;
    // Other roles: only while still pendiente
    return expense.status === "pendiente";
  };

  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";
  const projectCode = (id) => projects.find((p) => p.id === id)?.code || "—";
  const filtered = expenses;
  const [expandedId, setExpandedId] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const [viewerReceipt, setViewerReceipt] = useState(null);
  const [viewerEvidence, setViewerEvidence] = useState(null);
  const [backingUp, setBackingUp] = useState(false);

  const downloadBackup = async () => {
    if (backingUp) return;
    setBackingUp(true);
    try {
      const params = { scope: "obra" };
      if (filter) params.project_id = filter;
      if (filterStart) params.start_date = filterStart;
      if (filterEnd) params.end_date = filterEnd;
      const res = await api.get("/receipts/backup", { params, responseType: "blob", timeout: 180000 });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `respaldo_comprobantes_${new Date().toISOString().slice(0, 10)}.zip`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Respaldo ZIP descargado (comprobantes + índice CSV)");
    } catch (e) {
      toast.error(e.response?.status === 404 ? "No hay comprobantes en ese rango" : "No se pudo generar el respaldo");
    } finally {
      setBackingUp(false);
    }
  };

  const downloadExcel = async () => {
    try {
      const params = {};
      if (filter) params.project_id = filter;
      if (filterStatus) params.status = filterStatus;
      if (filterCategory) params.category = filterCategory;
      if (filterPayment) params.payment_method = filterPayment;
      if (filterStart) params.start_date = filterStart;
      if (filterEnd) params.end_date = filterEnd;
      const res = await api.get("/expenses/export/excel", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      link.download = `gastos_obra_${stamp}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch {
      toast.error("No se pudo descargar el Excel");
    }
  };

  return (
    <div data-testid="expenses-page">
      <PageHeader
        subtitle={lockedProjectId ? "Gastos de este proyecto" : (tab === "admin" ? "Gastos administrativos de la empresa" : "Control financiero por proyecto")}
        title={lockedProjectId ? "Gastos del proyecto" : "Gastos"}
        actions={
          tab === "obra" ? (
            <div className="flex items-center gap-2">
              {["admin", "contador"].includes(user?.role) && (
                <button
                  data-testid="receipts-backup-btn"
                  onClick={downloadBackup}
                  disabled={backingUp}
                  title="Descargar ZIP con todos los comprobantes (usa los filtros de proyecto y fechas)"
                  className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold"
                  style={{ borderColor: "var(--cyan)", color: "var(--cyan)" }}
                >
                  {backingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderOpen className="w-4 h-4" />} Respaldo
                </button>
              )}
              {["admin", "contador"].includes(user?.role) && (
                <button
                  data-testid="export-obra-excel-btn"
                  onClick={downloadExcel}
                  title="Descargar Excel con los filtros actuales"
                  className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold"
                  style={{ borderColor: "var(--green)", color: "var(--green)" }}
                >
                  <Download className="w-4 h-4" /> Excel
                </button>
              )}
              <button data-testid="new-expense-btn" onClick={() => { setEditingId(null); setForm({ ...empty, project_id: lockedProjectId || projects[0]?.id || "" }); setReceiptFile(null); setScanInfo(null); setOpen(true); }} className="btn-primary px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest">
                <Plus className="w-4 h-4" /> Registrar gasto
              </button>
            </div>
          ) : null
        }
      />
      <div className="px-6 lg:px-8 py-6">
        {/* Tabs */}
        {canSeeAdminTab && (
          <div className="mb-5 flex items-center gap-1 panel p-1 w-fit">
            <button
              data-testid="tab-obra"
              onClick={() => setTabAndUrl("obra")}
              className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2 transition-all ${tab === "obra" ? "" : "text-slate-400 hover:text-slate-200"}`}
              style={tab === "obra" ? {
                background: "linear-gradient(90deg, rgba(99,102,241,0.18), rgba(34,211,238,0.12))",
                color: "var(--cyan)",
                boxShadow: "inset 0 0 18px rgba(34,211,238,0.18)",
                border: "1px solid rgba(34,211,238,0.35)",
              } : { border: "1px solid transparent" }}
            >
              <HardHat className="w-4 h-4" /> Por Obra
            </button>
            <button
              data-testid="tab-admin"
              onClick={() => setTabAndUrl("admin")}
              className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2 transition-all ${tab === "admin" ? "" : "text-slate-400 hover:text-slate-200"}`}
              style={tab === "admin" ? {
                background: "linear-gradient(90deg, rgba(217,70,239,0.18), rgba(217,70,239,0.06))",
                color: "var(--magenta)",
                boxShadow: "inset 0 0 18px rgba(217,70,239,0.18)",
                border: "1px solid rgba(217,70,239,0.35)",
              } : { border: "1px solid transparent" }}
            >
              <Building2 className="w-4 h-4" /> Administrativos
            </button>
          </div>
        )}

        {tab === "admin" ? (
          <AdminExpensesTab />
        ) : (
        <>
        <div className="mb-4 panel p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="label-mono" style={{ color: "var(--cyan)" }}>// FILTROS</span>
            {(filter || filterStatus || filterCategory || filterPayment || filterStart || filterEnd || search) && (
              <button data-testid="clear-filters-btn" onClick={clearFilters} className="text-[10px] uppercase tracking-widest font-semibold hover:underline" style={{ color: "var(--magenta)" }}>
                Limpiar filtros
              </button>
            )}
          </div>
          <div className="relative mb-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <input data-testid="expenses-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por descripción, proveedor, folio o proyecto…"
              className="input-cyber w-full pl-9 pr-8 py-1.5 text-sm" />
            {search && (
              <button data-testid="expenses-search-clear" onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200">
                <XCircle className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            {!lockedProjectId && (
              <select data-testid="expense-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
                className="input-cyber px-3 py-1.5 text-sm">
                <option value="" style={{ background: "#0a0f1d" }}>Todos los proyectos</option>
                {projects.map((p) => <option key={p.id} value={p.id} style={{ background: "#0a0f1d" }}>{p.name}</option>)}
              </select>
            )}
            <select data-testid="filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="input-cyber px-3 py-1.5 text-sm">
              <option value="" style={{ background: "#0a0f1d" }}>Todos los estados</option>
              {EXPENSE_STATUS.map((s) => <option key={s.value} value={s.value} style={{ background: "#0a0f1d" }}>{s.label}</option>)}
            </select>
            <select data-testid="filter-category" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
              className="input-cyber px-3 py-1.5 text-sm">
              <option value="" style={{ background: "#0a0f1d" }}>Todas las categorías</option>
              {EXPENSE_CATEGORIES.map((c) => <option key={c.value} value={c.value} style={{ background: "#0a0f1d" }}>{c.label}</option>)}
            </select>
            <select data-testid="filter-payment-method" value={filterPayment} onChange={(e) => setFilterPayment(e.target.value)}
              className="input-cyber px-3 py-1.5 text-sm">
              <option value="" style={{ background: "#0a0f1d" }}>Todos los métodos</option>
              {EXPENSE_PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value} style={{ background: "#0a0f1d" }}>{m.label}</option>)}
            </select>
            <input type="date" data-testid="filter-start" value={filterStart} onChange={(e) => setFilterStart(e.target.value)}
              className="input-cyber px-3 py-1.5 text-sm" placeholder="Desde" />
            <input type="date" data-testid="filter-end" value={filterEnd} onChange={(e) => setFilterEnd(e.target.value)}
              className="input-cyber px-3 py-1.5 text-sm" placeholder="Hasta" />
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap px-1" data-testid="expenses-totals-bar">
          {(() => {
            const total = sums ? sums.total : filtered.reduce((a, x) => a + (x.amount || 0), 0);
            const pend = sums ? sums.pendiente : 0;
            const pagado = sums ? sums.pagado : 0;
            const chip = (label, val, color) => (
              <span className="px-2.5 py-1 rounded-sm text-[11px] font-mono" style={{ background: `${color}12`, color, border: `1px solid ${color}40` }}>
                {label}: <b>{fmtMXN(val)}</b>
              </span>
            );
            return (
              <>
                <span className="text-[11px] font-mono text-slate-400">{totalCount} gasto{totalCount === 1 ? "" : "s"}</span>
                {chip("Total", total, "#22d3ee")}
                {pend > 0 && chip("Pendiente", pend, "#fbbf24")}
                {pagado > 0 && chip("Pagado", pagado, "#22c55e")}
              </>
            );
          })()}
        </div>

        <div className="panel overflow-hidden">
          <div ref={topScrollRef} onScroll={syncScroll(topScrollRef, bodyScrollRef)} className="overflow-x-auto overflow-y-hidden" data-testid="expenses-top-scrollbar" style={{ height: 14 }}>
            <div style={{ width: tableScrollW, height: 1 }} />
          </div>
          <div ref={bodyScrollRef} onScroll={syncScroll(bodyScrollRef, topScrollRef)} className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]" data-testid="expenses-table">
            <thead className="panel-header">
              <tr className="text-left">
                <Th sticky>Acciones</Th>
                <Th sortKey="estado" sort={sort} onSort={toggleSort}>Estado</Th>
                <Th sortKey="proyecto" sort={sort} onSort={toggleSort}>Proyecto</Th>
                <Th>Recursos</Th>
                <Th sortKey="descripcion" sort={sort} onSort={toggleSort}>Descripción</Th>
                <Th right sortKey="monto" sort={sort} onSort={toggleSort}>Monto</Th>
                <Th sortKey="fecha" sort={sort} onSort={toggleSort}>Fecha</Th>
                <Th sortKey="proveedor" sort={sort} onSort={toggleSort}>Proveedor</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-slate-500">Sin gastos registrados.</td></tr>
              )}
              {filtered.map((e) => {
                const isOpen = expandedId === e.id;
                const vendorLabel = e.vendor_id ? (vendors.find((v) => v.id === e.vendor_id)?.name || e.vendor || "—") : (e.vendor || "—");
                const methodLabel = e.payment_method ? (EXPENSE_PAYMENT_METHODS.find((m) => m.value === e.payment_method)?.label || e.payment_method) : null;
                return [
                <tr key={e.id} data-testid={`expense-row-${e.id}`} className="row-hover cursor-pointer" style={{ borderBottom: isOpen ? "none" : "1px solid var(--border)" }}
                  onClick={() => { setExpandedId(isOpen ? null : e.id); setMenuId(null); }}>
                  <Td sticky z={menuId === e.id ? 30 : 5}>
                    <div className="flex items-center gap-0.5" onClick={(ev) => ev.stopPropagation()}>
                      {canApprove && e.status === "pendiente" && (
                        <>
                          <button data-testid={`approve-${e.id}`} onClick={() => setStatus(e.id, "aprobado")} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--green)" }} title="Aprobar"><Check className="w-4 h-4" /></button>
                          <button data-testid={`reject-${e.id}`} onClick={() => setStatus(e.id, "rechazado")} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }} title="Rechazar"><XCircle className="w-4 h-4" /></button>
                        </>
                      )}
                      <div className="relative">
                        <button data-testid={`menu-expense-${e.id}`} onClick={() => setMenuId(menuId === e.id ? null : e.id)}
                          className="p-1.5 rounded-sm hover:bg-white/5 text-slate-400 hover:text-slate-200">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        {menuId === e.id && (
                          <div className="absolute left-0 top-8 z-20 w-44 py-1 rounded-sm shadow-2xl" data-testid={`menu-dropdown-${e.id}`}
                            style={{ background: "#0d1424", border: "1px solid var(--border-2)" }}>
                            {canApprove && e.status === "aprobado" && (
                              <MenuItem testId={`pay-${e.id}`} color="var(--cyan)" onClick={() => { setMenuId(null); setStatus(e.id, "pagado"); }}>$ Marcar pagado</MenuItem>
                            )}
                            {canEditExpense(e) && (
                              <MenuItem testId={`edit-expense-${e.id}`} color="var(--cyan)" onClick={() => { setMenuId(null); startEdit(e); }}><Pencil className="w-3.5 h-3.5" /> Editar</MenuItem>
                            )}
                            {(e.change_history && e.change_history.length > 0) && (
                              <MenuItem testId={`history-expense-${e.id}`} color="var(--magenta)" onClick={() => { setMenuId(null); setHistoryExpense(e); }}><History className="w-3.5 h-3.5" /> Historial ({e.change_history.length})</MenuItem>
                            )}
                            {(user?.role === "admin" || user?.role === "contador") && (
                              <MenuItem testId={`delete-expense-${e.id}`} color="var(--red)" onClick={() => { setMenuId(null); remove(e.id); }}><Trash2 className="w-3.5 h-3.5" /> Eliminar</MenuItem>
                            )}
                          </div>
                        )}
                      </div>
                      <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </div>
                  </Td>
                  <Td><StatusBadge items={EXPENSE_STATUS} value={e.status} /></Td>
                  <Td>
                    <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-semibold whitespace-nowrap inline-block truncate max-w-[150px] align-middle" title={`${projectCode(e.project_id)} — ${projectName(e.project_id)}`}
                      style={{ background: "rgba(99,102,241,0.12)", color: "#a5b4fc", border: "1px solid rgba(99,102,241,0.3)" }}>
                      {projectName(e.project_id)}
                    </span>
                  </Td>
                  <Td data-testid={`expense-funded-${e.id}`}>
                    <div className="flex items-center gap-1.5 whitespace-nowrap text-xs">
                      <FundedByBadge funded_by={e.funded_by} funded_by_name={e.funded_by_name} funded_by_note={e.funded_by_note} />
                      {methodLabel && <span className="text-[10px] text-slate-500">· {methodLabel}</span>}
                    </div>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="font-semibold text-slate-100 truncate max-w-[260px]" title={e.description}>{e.description}</span>
                      {e.receipt_path && (
                        <button type="button" data-testid={`clip-receipt-${e.id}`} onClick={(ev) => { ev.stopPropagation(); setViewerReceipt({ fileId: e.receipt_path, title: e.description }); }} className="shrink-0" title="Ver comprobante">
                          <Paperclip className="w-3 h-3" style={{ color: "var(--cyan)" }} />
                        </button>
                      )}
                      {(e.evidence_photos || []).length > 0 && (
                        <button type="button" data-testid={`clip-evidence-${e.id}`} onClick={(ev) => { ev.stopPropagation(); setViewerEvidence({ photos: e.evidence_photos, title: e.description }); }} className="shrink-0 flex items-center gap-0.5" title={`Ver ${e.evidence_photos.length} evidencia(s)`}>
                          <Camera className="w-3 h-3" style={{ color: "#fbbf24" }} />
                          <span className="text-[9px] font-mono" style={{ color: "#fbbf24" }}>{e.evidence_photos.length}</span>
                        </button>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500 capitalize">{e.category.replace("_", " ")}{e.invoice_number ? ` · Folio ${e.invoice_number}` : ""}</div>
                  </Td>
                  <Td right><span className="font-mono font-semibold text-slate-100 whitespace-nowrap">{fmtMXN(e.amount)}</span></Td>
                  <Td><span className="font-mono text-xs text-slate-300 whitespace-nowrap">{fmtDate(e.expense_date)}</span></Td>
                  <Td><span className="text-xs text-slate-300 truncate block max-w-[150px]" title={vendorLabel}>{vendorLabel}</span></Td>
                </tr>,
                isOpen && (
                  <tr key={`${e.id}-detail`} data-testid={`expense-detail-${e.id}`}>
                    <td colSpan={8} className="px-6 py-3" style={{ background: "rgba(15,23,42,0.55)", borderBottom: "1px solid var(--border)" }}>
                      <div className="flex items-start gap-6 flex-wrap text-xs">
                        <div className="space-y-1">
                          <div className="label-mono text-[9px]">// TRAZABILIDAD</div>
                          {e.created_by_name && <div className="text-slate-400">Registró: <span className="text-slate-200">{e.created_by_name}</span> · {fmtDate(e.created_at)}</div>}
                          {e.approved_by_name && (
                            <div style={{ color: e.status === "rechazado" ? "var(--red)" : "var(--green)" }}>
                              {e.status === "rechazado" ? "✗ Rechazó" : "✓ Aprobó"}: {e.approved_by_name}{e.approved_by_role ? ` (${e.approved_by_role})` : ""} · {fmtDate(e.approved_at)}
                            </div>
                          )}
                          {e.paid_by_name && <div style={{ color: "var(--cyan)" }}>$ Pagó: {e.paid_by_name} · {fmtDate(e.paid_at)}</div>}
                        </div>
                        <div className="space-y-1">
                          <div className="label-mono text-[9px]">// DETALLE</div>
                          <div className="text-slate-400">Proyecto: <span className="text-slate-200">{projectName(e.project_id)}</span></div>
                          {e.funded_by === "persona" && e.funded_by_name && <div className="text-slate-400">Fuente: <span className="text-slate-200">{e.funded_by_name}</span>{e.funded_by_note ? ` — ${e.funded_by_note}` : ""}</div>}
                          {e.requires_invoice && (
                            <div className="text-slate-400">Factura: <span style={{ color: e.invoice_received ? "var(--green)" : "#fbbf24" }}>{e.invoice_received ? `Recibida${e.invoice_uuid ? ` · ${e.invoice_uuid.slice(0, 13)}…` : ""}` : "Pendiente de recibir"}</span></div>
                          )}
                        </div>
                        {(e.items || []).length > 0 && (
                          <div className="space-y-1 min-w-[280px]" data-testid={`expense-items-${e.id}`}>
                            <div className="label-mono text-[9px]">// PARTIDAS DE LA COMPRA ({e.items.length})</div>
                            {e.items.map((it, i) => (
                              <div key={`${e.id}-${i}-${it.product}`} className="flex items-center gap-2 text-slate-300">
                                <span className="font-mono text-slate-500 w-14 shrink-0">{it.qty} {it.unit || ""}</span>
                                <span className="flex-1 truncate" title={it.product}>{it.product}</span>
                                <span className="font-mono text-slate-500">{fmtMXN(it.unit_price)}</span>
                                <span className="font-mono font-semibold text-slate-100">{fmtMXN(it.amount)}</span>
                              </div>
                            ))}
                            <div className="text-right font-mono" style={{ color: "var(--cyan)" }}>Suma: {fmtMXN(e.items.reduce((s, it) => s + (Number(it.amount) || 0), 0))}</div>
                          </div>
                        )}
                        {e.invoice_details && Object.values(e.invoice_details).some((v) => v != null && v !== "") && (
                          <div className="space-y-1" data-testid={`expense-fiscal-${e.id}`}>
                            <div className="label-mono text-[9px]">// DATOS FISCALES (CFDI)</div>
                            {e.invoice_details.emisor_name && <div className="text-slate-400">Emisor: <span className="text-slate-200">{e.invoice_details.emisor_name}</span>{e.invoice_details.rfc_emisor ? ` · ${e.invoice_details.rfc_emisor}` : ""}</div>}
                            {!e.invoice_details.emisor_name && e.invoice_details.rfc_emisor && <div className="text-slate-400">RFC: <span className="text-slate-200">{e.invoice_details.rfc_emisor}</span></div>}
                            {e.invoice_details.folio_fiscal && <div className="text-slate-400">Folio fiscal: <span className="font-mono text-slate-200">{e.invoice_details.folio_fiscal}</span></div>}
                            {(e.invoice_details.forma_pago || e.invoice_details.uso_cfdi) && <div className="text-slate-400">{e.invoice_details.forma_pago || ""}{e.invoice_details.forma_pago && e.invoice_details.uso_cfdi ? " · " : ""}{e.invoice_details.uso_cfdi || ""}</div>}
                            {(e.invoice_details.subtotal != null || e.invoice_details.iva != null) && (
                              <div className="font-mono text-slate-400">Sub {fmtMXN(e.invoice_details.subtotal || 0)} · IVA {fmtMXN(e.invoice_details.iva || 0)} · <span style={{ color: "var(--cyan)" }}>Total {fmtMXN(e.invoice_details.total ?? e.amount)}</span></div>
                            )}
                          </div>
                        )}
                        {e.receipt_path && (
                          <button
                            type="button"
                            data-testid={`view-receipt-${e.id}`}
                            onClick={(ev) => { ev.stopPropagation(); setViewerReceipt({ fileId: e.receipt_path, title: e.description }); }}
                            className="hover:underline flex items-center gap-1 self-center px-3 py-1.5 rounded-sm" style={{ color: "var(--cyan)", border: "1px solid rgba(34,211,238,0.3)" }}>
                            <Paperclip className="w-3.5 h-3.5" /> Ver comprobante
                          </button>
                        )}
                        {(e.evidence_photos || []).length > 0 && (
                          <button
                            type="button"
                            data-testid={`view-evidence-${e.id}`}
                            onClick={(ev) => { ev.stopPropagation(); setViewerEvidence({ photos: e.evidence_photos, title: e.description }); }}
                            className="hover:underline flex items-center gap-1 self-center px-3 py-1.5 rounded-sm" style={{ color: "#fbbf24", border: "1px solid rgba(251,191,36,0.3)" }}>
                            <Camera className="w-3.5 h-3.5" /> Evidencias ({e.evidence_photos.length})
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ),
              ];})}
            </tbody>
          </table>
          </div>
          {totalCount > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-2.5" style={{ borderTop: "1px solid var(--border)" }} data-testid="expenses-pagination">
              <button data-testid="expenses-prev-page" disabled={page === 0} onClick={() => setPage(page - 1)}
                className="btn-ghost px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold rounded-sm disabled:opacity-40" style={{ border: "1px solid var(--border)" }}>
                ← Anterior
              </button>
              <span className="text-xs font-mono text-slate-400">
                Página <span className="text-slate-100 font-bold">{page + 1}</span> de {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
                <span className="text-slate-500 ml-2">({page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} de {totalCount})</span>
              </span>
              <button data-testid="expenses-next-page" disabled={(page + 1) * PAGE_SIZE >= totalCount} onClick={() => setPage(page + 1)}
                className="btn-ghost px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold rounded-sm disabled:opacity-40" style={{ border: "1px solid var(--border)" }}>
                Siguiente →
              </button>
            </div>
          )}
        </div>
        </>
        )}
      </div>

      {open && (
        <Modal
          onClose={() => { setOpen(false); setEditingId(null); }}
          title={editingId ? "Editar gasto" : "Nuevo gasto"}
          footer={
            <>
              <button type="button" onClick={() => { setOpen(false); setEditingId(null); }} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" form="expense-form" data-testid="save-expense-btn" disabled={savingExpense} className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">{savingExpense ? "Guardando…" : editingId ? "Actualizar" : "Guardar"}</button>
            </>
          }
        >
          <form id="expense-form" onSubmit={save} className="space-y-4">
            {!navigator.onLine && (
              <div className="px-3 py-2 rounded-sm flex items-center gap-2 text-xs" style={{ background: "rgba(220,38,38,0.12)", border: "1px solid #dc2626", color: "#fca5a5" }}>
                <CloudOff className="w-4 h-4 flex-shrink-0" />
                <span>Sin conexión. El gasto se guardará localmente y se enviará al volver internet.</span>
              </div>
            )}

            {/* AI Receipt Scanner */}
            <div className="panel p-3" style={{ background: "linear-gradient(135deg, rgba(217,70,239,0.08), rgba(34,211,238,0.08))", border: "1px dashed var(--magenta)" }}>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <Sparkles className="w-5 h-5" style={{ color: "var(--magenta)" }} />
                </div>
                <div className="flex-1">
                  <div className="label-mono mb-1" style={{ color: "var(--magenta)" }}>// ESCÁNER IA</div>
                  <div className="text-xs text-slate-300 mb-2">
                    Toma o sube una foto del ticket y la IA llenará proveedor, monto, fecha, folio y categoría automáticamente.
                  </div>
                  <input
                    ref={scanRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={(e) => { const f = e.target.files[0]; if (f) handleScan(f); e.target.value = ""; }}
                    className="hidden"
                    data-testid="exp-scan-input"
                  />
                  <input
                    ref={cameraRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => { const f = e.target.files[0]; if (f) handleScan(f); e.target.value = ""; }}
                    className="hidden"
                    data-testid="exp-camera-input"
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      data-testid="exp-camera-btn"
                      onClick={() => cameraRef.current?.click()}
                      disabled={scanning || !navigator.onLine}
                      className="btn-primary px-3 py-1.5 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold disabled:opacity-50"
                      title="Abre la cámara del dispositivo"
                    >
                      {scanning ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Analizando…</>
                      ) : (
                        <><Camera className="w-4 h-4" /> Tomar foto</>
                      )}
                    </button>
                    <button
                      type="button"
                      data-testid="exp-scan-btn"
                      onClick={() => scanRef.current?.click()}
                      disabled={scanning || !navigator.onLine}
                      className="btn-ghost px-3 py-1.5 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold disabled:opacity-50"
                      title="Selecciona una imagen o PDF guardado"
                    >
                      <FolderOpen className="w-4 h-4" /> Subir archivo
                    </button>
                  </div>
                  {scanInfo && (
                    <div data-testid="exp-scan-result" className="mt-2 text-[11px] text-slate-300 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span style={{ color: "var(--cyan)" }}>✓ Detectado:</span>
                      {scanInfo.vendor && <span><strong className="text-slate-100">{scanInfo.vendor}</strong>{scanInfo.matchedVendor && <span className="ml-1" style={{ color: "var(--green)" }}>(vinculado)</span>}</span>}
                      {scanInfo.rfc && <span>RFC: <span className="font-mono">{scanInfo.rfc}</span></span>}
                      {scanInfo.amount != null && <span>Monto: <span className="font-mono">{fmtMXN(scanInfo.amount)}</span></span>}
                    </div>
                  )}
                  {scanInfo?.proposal && !scanInfo.matchedVendor && (
                    <div data-testid="vendor-match-proposal" className="mt-2 p-2 rounded-sm text-[11px] flex items-center gap-2 flex-wrap" style={{ border: "1px solid var(--cyan)", background: "rgba(34,211,238,0.06)" }}>
                      <span className="text-slate-300">¿Es el proveedor <strong className="text-slate-100">{scanInfo.proposal.name}</strong>{scanInfo.proposal.rfc ? ` (${scanInfo.proposal.rfc})` : ""}? <span className="text-slate-500">coincidencia por {scanInfo.proposal.via}</span></span>
                      <button type="button" data-testid="vendor-match-link" onClick={() => { setForm((f) => ({ ...f, vendor_id: scanInfo.proposal.id, vendor: "" })); setScanInfo((s) => ({ ...s, matchedVendor: true })); toast.success(`Vinculado a ${scanInfo.proposal.name}`); }}
                        className="btn-primary px-2 py-1 text-[10px] uppercase tracking-widest">Vincular</button>
                      <button type="button" data-testid="vendor-match-dismiss" onClick={() => setScanInfo((s) => ({ ...s, proposal: null }))}
                        className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ border: "1px solid var(--border)" }}>No es</button>
                    </div>
                  )}
                  {scanInfo && !scanInfo.proposal && !scanInfo.matchedVendor && scanInfo.vendor && !form.vendor_id && (
                    <div data-testid="vendor-create-proposal" className="mt-2 p-2 rounded-sm text-[11px] flex items-center gap-2 flex-wrap" style={{ border: "1px solid #f59e0b", background: "rgba(245,158,11,0.06)" }}>
                      <span className="text-slate-300"><strong className="text-slate-100">{scanInfo.vendor}</strong> no está en el directorio.</span>
                      <button type="button" data-testid="vendor-create-quick" onClick={async () => {
                        try {
                          const { data: nv } = await api.post("/vendors", { name: scanInfo.vendor, rfc: scanInfo.rfc || undefined, category: "materiales" });
                          setForm((f) => ({ ...f, vendor_id: nv.id, vendor: "" }));
                          setScanInfo((s) => ({ ...s, matchedVendor: true, proposal: null }));
                          toast.success(`Proveedor ${nv.name} dado de alta y vinculado`);
                        } catch (err) { toast.error(err.response?.data?.detail || "Error al dar de alta"); }
                      }} className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "#f59e0b", border: "1px solid #f59e0b" }}>Dar de alta y vincular</button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {lockedProjectId ? (
                <div>
                  <label className="label-mono block mb-1.5">Proyecto</label>
                  <div
                    data-testid="exp-project-locked"
                    className="input-cyber px-3 py-2 text-sm text-slate-400"
                    style={{ background: "rgba(15,23,42,0.7)", borderColor: "var(--border)" }}
                  >
                    {(() => {
                      const p = projects.find((x) => x.id === lockedProjectId);
                      return p ? p.name : "Cargando…";
                    })()}
                  </div>
                </div>
              ) : (
                <Sel label="Proyecto" testId="exp-project" value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v, budget_item_id: "" })}
                  options={projects.map((p) => ({ value: p.id, label: p.name }))} />
              )}
              <Sel label="Categoría" testId="exp-category" value={form.category} onChange={(v) => setForm({ ...form, category: v })}
                options={EXPENSE_CATEGORIES} />
            </div>
            <In label="Descripción" testId="exp-description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />

            {budgetItems.length > 0 && (
              <Sel
                label={`Partida del presupuesto (opcional · ${budgetItems.length} disponibles)`}
                testId="exp-budget-item"
                value={form.budget_item_id || ""}
                onChange={(v) => setForm({ ...form, budget_item_id: v })}
                options={[
                  { value: "", label: "— Sin vincular a partida —" },
                  ...budgetItems.map((it) => ({ value: it.id, label: `${it.code || ""} ${(it.description || "").slice(0, 80)}` })),
                ]}
              />
            )}
            <div className="grid grid-cols-3 gap-3">
              <In label="Monto MXN" testId="exp-amount" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} />
              <Sel label="Proveedor" testId="exp-vendor-select" value={form.vendor_id || ""} onChange={(v) => setForm({ ...form, vendor_id: v, vendor: v ? "" : form.vendor })}
                options={[...vendors.filter((vv) => vv.active).map((vv) => ({ value: vv.id, label: vv.name })), { value: "", label: "Otro / texto libre" }]} />
              <In label="Folio/Factura" testId="exp-invoice" value={form.invoice_number} onChange={(v) => setForm({ ...form, invoice_number: v })} required={false} />
            </div>
            {!form.vendor_id && (
              <In label="Nombre del proveedor (otro)" testId="exp-vendor-text" value={form.vendor} onChange={(v) => setForm({ ...form, vendor: v })} required={false} />
            )}
            <In label="Fecha del gasto" testId="exp-date" type="date" value={form.expense_date} onChange={(v) => setForm({ ...form, expense_date: v })} />

            {/* Detalle de compra (partidas) + datos fiscales CFDI */}
            <div className="panel p-3 space-y-3" style={{ background: "rgba(8,12,24,0.6)", borderColor: "rgba(34,211,238,0.25)" }} data-testid="exp-items-section">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="label-mono" style={{ color: "var(--cyan)" }}>// DETALLE DE COMPRA (OPCIONAL)</div>
                <button type="button" data-testid="exp-add-item" onClick={() => setForm({ ...form, items: [...(form.items || []), { _k: rowKey(), qty: 1, product: "", unit: "PZA", unit_price: "", amount: "" }] })}
                  className="text-[11px] label-mono hover:underline" style={{ color: "var(--cyan)" }}>+ AGREGAR PARTIDA</button>
              </div>
              {(form.items || []).length > 0 && (
                <div className="space-y-1.5">
                  <div className="hidden sm:grid gap-1.5 text-[9px] label-mono" style={{ gridTemplateColumns: "55px 1fr 65px 85px 85px 22px", color: "var(--text-mute)" }}>
                    <span>CANT</span><span>PRODUCTO</span><span>UNIDAD</span><span className="text-right">P. UNIT</span><span className="text-right">IMPORTE</span><span />
                  </div>
                  {form.items.map((it, i) => (
                    <div key={it._k || i} className="grid gap-1.5 items-center" style={{ gridTemplateColumns: "55px 1fr 65px 85px 85px 22px" }}>
                      <input type="number" step="0.01" min="0" value={it.qty} data-testid={`exp-item-qty-${i}`}
                        onChange={(e) => setForm({ ...form, items: form.items.map((x, idx) => idx === i ? { ...x, qty: e.target.value, amount: Math.round((Number(e.target.value) || 0) * (Number(x.unit_price) || 0) * 100) / 100 } : x) })}
                        className="input-cyber px-1.5 py-1.5 text-xs text-center" />
                      <input value={it.product} placeholder="Producto / concepto" data-testid={`exp-item-product-${i}`}
                        onChange={(e) => setForm({ ...form, items: form.items.map((x, idx) => idx === i ? { ...x, product: e.target.value } : x) })}
                        className="input-cyber px-2 py-1.5 text-xs" />
                      <input value={it.unit || ""} placeholder="PZA" data-testid={`exp-item-unit-${i}`}
                        onChange={(e) => setForm({ ...form, items: form.items.map((x, idx) => idx === i ? { ...x, unit: e.target.value.toUpperCase() } : x) })}
                        className="input-cyber px-1.5 py-1.5 text-xs text-center uppercase" />
                      <input type="number" step="0.01" min="0" value={it.unit_price} placeholder="0.00" data-testid={`exp-item-price-${i}`}
                        onChange={(e) => setForm({ ...form, items: form.items.map((x, idx) => idx === i ? { ...x, unit_price: e.target.value, amount: Math.round((Number(x.qty) || 0) * (Number(e.target.value) || 0) * 100) / 100 } : x) })}
                        className="input-cyber px-1.5 py-1.5 text-xs text-right" />
                      <input type="number" step="0.01" min="0" value={it.amount} placeholder="0.00" data-testid={`exp-item-amount-${i}`}
                        onChange={(e) => setForm({ ...form, items: form.items.map((x, idx) => idx === i ? { ...x, amount: e.target.value } : x) })}
                        className="input-cyber px-1.5 py-1.5 text-xs text-right" />
                      <button type="button" data-testid={`exp-item-remove-${i}`} onClick={() => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) })}
                        className="text-slate-500 hover:text-red-400"><XCircle className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-1 flex-wrap gap-2">
                    <span className="text-[11px] font-mono text-slate-400" data-testid="exp-items-sum">
                      Suma partidas: <b style={{ color: "var(--cyan)" }}>{fmtMXN(form.items.reduce((s, it) => s + (Number(it.amount) || 0), 0))}</b>
                    </span>
                    <button type="button" data-testid="exp-use-items-total"
                      onClick={() => {
                        const t = form.invoice_details?.total !== "" && form.invoice_details?.total != null ? Number(form.invoice_details.total) : form.items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
                        setForm({ ...form, amount: Math.round(t * 100) / 100 });
                      }}
                      className="text-[10px] label-mono hover:underline" style={{ color: "var(--green)" }}>USAR COMO MONTO DEL GASTO</button>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <In label="RFC emisor" testId="exp-fiscal-rfc" required={false} value={form.invoice_details?.rfc_emisor || ""} onChange={(v) => setForm({ ...form, invoice_details: { ...emptyFiscal, ...form.invoice_details, rfc_emisor: v.toUpperCase() } })} />
                <In label="Nombre emisor" testId="exp-fiscal-emisor" required={false} value={form.invoice_details?.emisor_name || ""} onChange={(v) => setForm({ ...form, invoice_details: { ...emptyFiscal, ...form.invoice_details, emisor_name: v } })} />
                <In label="Folio fiscal (UUID)" testId="exp-fiscal-uuid" required={false} value={form.invoice_details?.folio_fiscal || ""} onChange={(v) => setForm({ ...form, invoice_details: { ...emptyFiscal, ...form.invoice_details, folio_fiscal: v } })} />
                <In label="Forma de pago" testId="exp-fiscal-forma" required={false} value={form.invoice_details?.forma_pago || ""} onChange={(v) => setForm({ ...form, invoice_details: { ...emptyFiscal, ...form.invoice_details, forma_pago: v } })} />
                <In label="Uso CFDI" testId="exp-fiscal-uso" required={false} value={form.invoice_details?.uso_cfdi || ""} onChange={(v) => setForm({ ...form, invoice_details: { ...emptyFiscal, ...form.invoice_details, uso_cfdi: v } })} />
                <div className="grid grid-cols-3 gap-1.5 col-span-2 sm:col-span-3">
                  <In label="Subtotal" testId="exp-fiscal-subtotal" type="number" required={false} value={form.invoice_details?.subtotal ?? ""} onChange={(v) => setForm({ ...form, invoice_details: { ...emptyFiscal, ...form.invoice_details, subtotal: v } })} />
                  <In label="IVA" testId="exp-fiscal-iva" type="number" required={false} value={form.invoice_details?.iva ?? ""} onChange={(v) => setForm({ ...form, invoice_details: { ...emptyFiscal, ...form.invoice_details, iva: v } })} />
                  <In label="Total" testId="exp-fiscal-total" type="number" required={false} value={form.invoice_details?.total ?? ""} onChange={(v) => setForm({ ...form, invoice_details: { ...emptyFiscal, ...form.invoice_details, total: v } })} />
                </div>
              </div>
              <p className="text-[10px] text-slate-500">Escanea la factura con IA para llenar las partidas y datos fiscales automáticamente, o captúralos a mano. Todo es opcional.</p>
            </div>

            <div className="panel p-3 space-y-3" style={{ background: "rgba(8,12,24,0.6)" }}>
              <div className="label-mono" style={{ color: "var(--cyan)" }}>// FUENTE DE RECURSOS</div>
              <div className="grid grid-cols-2 gap-3">
                <Sel
                  label="¿Quién pone el recurso?"
                  testId="exp-funded-by"
                  value={form.funded_by}
                  onChange={(v) => setForm({ ...form, funded_by: v, funded_by_name: v === "empresa" ? "" : form.funded_by_name })}
                  options={EXPENSE_FUNDED_BY}
                />
                {form.funded_by === "persona" && (
                  <div>
                    <label className="label-mono block mb-1.5">Nombre de la persona *</label>
                    <select
                      data-testid="exp-funded-by-name-select"
                      required
                      value={
                        // Si el nombre actual coincide con un inversionista, usa ese valor;
                        // si no, asume "Otra persona" (texto libre)
                        investors.some((i) => i.name === form.funded_by_name)
                          ? form.funded_by_name
                          : (form.funded_by_name ? "__other__" : "")
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "__other__") {
                          setForm({ ...form, funded_by_name: form.funded_by_name && !investors.some(i => i.name === form.funded_by_name) ? form.funded_by_name : " " });
                        } else {
                          setForm({ ...form, funded_by_name: val });
                        }
                      }}
                      className="input-cyber px-3 py-2 text-sm w-full"
                    >
                      <option value="" style={{ background: "#0a0f1d" }}>— Selecciona persona —</option>
                      {investors.length > 0 && (
                        <optgroup label="Inversionistas registrados">
                          {investors.map((inv) => (
                            <option key={inv.id} value={inv.name} style={{ background: "#0a0f1d" }}>
                              {inv.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      <option value="__other__" style={{ background: "#0a0f1d" }}>
                        Otra persona (escribir nombre)…
                      </option>
                    </select>
                    {form.funded_by_name &&
                      !investors.some((i) => i.name === form.funded_by_name) && (
                      <input
                        data-testid="exp-funded-by-name"
                        required
                        placeholder="Escribe el nombre completo"
                        value={form.funded_by_name.trim()}
                        onChange={(ev) => setForm({ ...form, funded_by_name: ev.target.value })}
                        className="input-cyber px-3 py-2 text-sm w-full mt-2"
                      />
                    )}
                    {investors.length === 0 && (
                      <p className="text-[11px] text-slate-500 mt-1">
                        {["admin", "contador"].includes(user?.role) ? (
                          <>Aún no hay inversionistas registrados. <a href="/investors" className="text-cyan-300 underline">Agregar uno</a>.</>
                        ) : (
                          <>Aún no hay inversionistas registrados. Pide al administrador que los agregue.</>
                        )}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="label-mono block mb-1.5">Nota de la fuente del recurso (opcional)</label>
                <textarea
                  data-testid="exp-funded-by-note"
                  rows={2}
                  placeholder="Ej. préstamo a reembolsar la próxima semana, acuerdo con el inversionista, etc."
                  value={form.funded_by_note}
                  onChange={(ev) => setForm({ ...form, funded_by_note: ev.target.value })}
                  className="input-cyber px-3 py-2 text-sm w-full resize-none"
                />
              </div>
              <Sel
                label="Método de pago"
                testId="exp-payment-method"
                value={form.payment_method || "efectivo"}
                onChange={(v) => setForm({ ...form, payment_method: v })}
                options={EXPENSE_PAYMENT_METHODS}
              />
              {canAssignCash && form.payment_method === "efectivo" && form.funded_by === "empresa" && cashHolders.length > 0 && (
                <div className="panel p-2.5" style={{ background: "rgba(34,211,238,0.05)", borderColor: "rgba(34,211,238,0.25)" }}>
                  <Sel
                    label="¿Es comprobación de efectivo entregado?"
                    testId="exp-cash-settlement"
                    value={form.cash_settlement_user_id || ""}
                    onChange={(v) => setForm({ ...form, cash_settlement_user_id: v })}
                    options={[
                      { value: "", label: "No — sale del saldo en caja general" },
                      ...cashHolders.map((h) => ({ value: h.id, label: `Sí — comprobación de ${h.name}` })),
                    ]}
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    Si eliges a alguien, este gasto descuenta de SU saldo por comprobar (el efectivo ya salió de caja al entregárselo). Si eliges "No", descuenta directo del saldo en caja.
                  </p>
                </div>
              )}
              <p className="text-[11px] text-slate-500">
                Selecciona <strong>Empresa</strong> cuando el gasto sale de los recursos de la constructora.
                Selecciona <strong>Persona</strong> cuando alguien adelantó el dinero de su bolsillo (se le deberá reembolsar).
              </p>

              {/* CFDI proveedor: ¿requiere factura? */}
              <div className="mt-2 panel p-2.5" style={{ background: "rgba(217,70,239,0.05)", borderColor: "rgba(217,70,239,0.25)" }}>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="exp-requires-invoice"
                    checked={!!form.requires_invoice}
                    onChange={(e) => setForm({ ...form, requires_invoice: e.target.checked })}
                    className="w-4 h-4 mt-0.5 accent-magenta-400"
                  />
                  <div>
                    <div className="text-sm font-semibold text-slate-100">Requiere factura (CFDI del proveedor)</div>
                    <p className="text-[11px] text-slate-400 leading-snug">
                      Marca esta opción si esperas que el proveedor te emita un CFDI por este gasto.
                      Aparecerá en el módulo "Facturas Pendientes" hasta que captures el UUID y folio.
                    </p>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer mt-2 pt-2" style={{ borderTop: "1px solid var(--border)" }}>
                  <input
                    type="checkbox"
                    data-testid="exp-invoice-not-required"
                    checked={!!form.invoice_not_required}
                    onChange={(e) => setForm({ ...form, invoice_not_required: e.target.checked })}
                    className="w-4 h-4 mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-semibold text-slate-100">No requiere factura</div>
                    <p className="text-[11px] text-slate-400 leading-snug">
                      Por defecto todo gasto con proveedor entra al seguimiento de comprobantes pendientes.
                      Marca esto para excluirlo (compras menores, viáticos, etc.).
                    </p>
                  </div>
                </label>
              </div>
            </div>

            <div>
              <label className="label-mono block mb-1.5">Comprobante (imagen o PDF)</label>
              <div className="border border-dashed rounded-sm p-5 text-center cursor-pointer transition-colors hover:bg-white/5"
                style={{ borderColor: "var(--border-2)" }}
                onClick={() => fileRef.current?.click()}>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={(e) => setReceiptFile(e.target.files[0])} className="hidden" data-testid="exp-receipt-input" />
                <Upload className="w-5 h-5 mx-auto mb-1.5" style={{ color: "var(--cyan)" }} />
                <div className="text-xs text-slate-300">{receiptFile ? receiptFile.name : "Click para subir comprobante (max 10 MB)"}</div>
              </div>
            </div>

            <div>
              <label className="label-mono block mb-1.5">Evidencias de compra (fotos)</label>
              <div className="border border-dashed rounded-sm p-4 text-center cursor-pointer transition-colors hover:bg-white/5"
                style={{ borderColor: "rgba(251,191,36,0.35)" }}
                onClick={() => evidenceRef.current?.click()}>
                <input ref={evidenceRef} type="file" accept="image/*" multiple onChange={(ev) => { const files = Array.from(ev.target.files || []); setEvidenceFiles((prev) => [...prev, ...files]); ev.target.value = ""; }} className="hidden" data-testid="exp-evidence-input" />
                <Camera className="w-5 h-5 mx-auto mb-1.5" style={{ color: "#fbbf24" }} />
                <div className="text-xs text-slate-300">
                  {evidenceFiles.length > 0 ? `${evidenceFiles.length} foto(s) por subir` : "Fotos de la llegada del material (tabique, cemento, etc.)"}
                  {(form.evidence_photos || []).length > 0 && <span className="text-slate-500"> · {form.evidence_photos.length} ya guardada(s)</span>}
                </div>
              </div>
              {evidenceFiles.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {evidenceFiles.map((f, i) => (
                    <span key={`${f.name}-${f.size}-${f.lastModified}`} className="px-2 py-0.5 rounded-sm text-[10px] font-mono flex items-center gap-1.5" style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.3)", color: "#fbbf24" }}>
                      {f.name.slice(0, 20)}
                      <button type="button" data-testid={`remove-evidence-${i}`} onClick={() => setEvidenceFiles(evidenceFiles.filter((_, j) => j !== i))} className="font-bold">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </form>
        </Modal>
      )}

      {viewerReceipt && (
        <ReceiptViewer fileId={viewerReceipt.fileId} title={viewerReceipt.title} onClose={() => setViewerReceipt(null)} />
      )}

      {viewerEvidence && (
        <EvidenceGallery photos={viewerEvidence.photos} title={viewerEvidence.title} onClose={() => setViewerEvidence(null)} />
      )}

      {historyExpense && (
        <Modal onClose={() => setHistoryExpense(null)} title="Historial de cambios">
          <div className="mb-3 panel p-3" style={{ background: "rgba(34,211,238,0.05)" }}>
            <div className="label-mono text-[10px]" style={{ color: "var(--cyan)" }}>// GASTO</div>
            <div className="text-sm font-semibold text-slate-100 mt-1">{historyExpense.description}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {fmtMXN(historyExpense.amount)} · {projectName(historyExpense.project_id)} · {fmtDate(historyExpense.expense_date)}
            </div>
          </div>
          <ChangeHistory history={historyExpense.change_history || []} />
        </Modal>
      )}

      {/* FAB "Capturar recibo" — solo en página por proyecto */}
      {lockedProjectId && (
        <>
          <input
            ref={fabCameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => { const f = e.target.files[0]; if (f) handleScan(f); e.target.value = ""; }}
            className="hidden"
            data-testid="fab-camera-input"
          />
          <button
            data-testid="fab-capture-receipt"
            onClick={() => fabCameraRef.current?.click()}
            disabled={scanning}
            className="fixed z-30 flex items-center gap-2 px-5 py-4 rounded-full shadow-2xl transition-all hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed"
            style={{
              bottom: "calc(1.5rem + env(safe-area-inset-bottom))",
              right: "1.5rem",
              background: "linear-gradient(135deg, var(--cyan), var(--magenta))",
              color: "#0a0f1d",
              boxShadow: "0 8px 32px rgba(34, 211, 238, 0.4), 0 0 24px rgba(217, 70, 239, 0.3)",
              minWidth: 64,
              minHeight: 64,
            }}
            aria-label="Capturar recibo con cámara"
            title="Capturar recibo con cámara (IA)"
          >
            {scanning ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="hidden sm:inline font-bold uppercase tracking-widest text-xs">Procesando…</span>
              </>
            ) : (
              <>
                <Camera className="w-6 h-6" />
                <span className="hidden sm:inline font-bold uppercase tracking-widest text-xs">Capturar recibo</span>
              </>
            )}
          </button>
        </>
      )}
    </div>
  );
}

const Th = ({ children, right, sticky, sortKey, sort, onSort }) => {
  const active = sortKey && sort?.key === sortKey;
  const arrow = active ? (sort.dir === 1 ? " ▲" : " ▼") : "";
  return (
    <th className={`label-mono px-4 py-3 ${right ? "text-right" : ""} ${sortKey ? "cursor-pointer select-none hover:text-slate-200" : ""}`}
      data-testid={sortKey ? `sort-${sortKey}` : undefined}
      onClick={sortKey ? () => onSort(sortKey) : undefined}
      style={sticky ? { position: "sticky", left: 0, zIndex: 6, background: "var(--bg-2)", boxShadow: "6px 0 8px -6px rgba(0,0,0,0.7)" } : active ? { color: "var(--cyan)" } : undefined}>
      {children}{arrow && <span style={{ color: "var(--cyan)" }}>{arrow}</span>}
    </th>
  );
};
const Td = ({ children, right, className = "", sticky, z, ...rest }) => (
  <td className={`px-4 py-3 align-top ${right ? "text-right" : ""} ${className}`}
    style={sticky ? { position: "sticky", left: 0, zIndex: z || 5, background: "var(--panel)", boxShadow: "6px 0 8px -6px rgba(0,0,0,0.7)" } : undefined} {...rest}>{children}</td>
);

const MenuItem = ({ children, onClick, color, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className="w-full text-left px-3 py-2 text-xs font-semibold flex items-center gap-2 hover:bg-white/5"
    style={{ color }}
  >
    {children}
  </button>
);

const FundedByBadge = ({ funded_by, funded_by_name, funded_by_note }) => {
  const isPersona = funded_by === "persona";
  const color = isPersona ? "var(--magenta)" : "var(--cyan)";
  const label = isPersona ? "PERSONA" : "EMPRESA";
  return (
    <div className="flex flex-col gap-0.5">
      <span
        className="label-mono text-[9px] px-1.5 py-0.5 rounded-sm inline-block w-fit"
        style={{ color, border: `1px solid ${color}`, background: "rgba(0,0,0,0.25)" }}
      >
        {label}
      </span>
      {isPersona && funded_by_name && (
        <span className="text-[11px] text-slate-300 truncate max-w-[140px]" title={funded_by_note ? `${funded_by_name} — ${funded_by_note}` : funded_by_name}>
          {funded_by_name}
        </span>
      )}
    </div>
  );
};
